import { Divider, Grid } from '@mui/material';
import { ClassNameMap, withStyles } from '@mui/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { useMemo } from 'react';
import Receipt from '../../../assets/icons/Receipt.svg';
import { PricingType, RuleTypes } from '../../../stores/types/price.interface';
import { formatDiscountPercentageLabel } from './promo-code/promo-code.utils';
import {
  getTotalPrice,
  isChargingItem,
  isIdleItem,
  isSessionFeeItem,
} from '../charger-pricing/utils';
import { ChargerDtoV2, CostBreakdownItem, Session } from '../dto/charger.dto';
import { ReceiptDto, ReceiptLineItemDto } from '../dto/receipt.dto';
import SessionListItem from './session-list-Item.component';
import { DetailsStyles } from './styles/detailsStyles';
import { sessionPricingType } from './utils';
import { dataTestIds } from '../../../constant/dataTestId';

interface IProps {
  classes: ClassNameMap;
  session: Session;
  isChargingBySelf?: boolean;
  charger: ChargerDtoV2;
  receipt?: ReceiptDto;
}

const ChargerSessionDetails = ({
  classes,
  session,
  isChargingBySelf = false,
  charger,
  receipt,
}: IProps) => {
  const { i18n } = useTranslation();
  const activeSessionLineItems = session?.runningTotals?.costBreakdown || [];
  const receiptChargingLineItems = receipt?.chargingLineItems || [];
  const receiptIdleLineItems = receipt?.idleLineItems || [];

  const fixedPrice = (value) => value?.toFixed(2);

  // calculate sum of accumulated price of charing sessions
  // If there is no receipt, session is still active and we will use activeSessionLineItems
  // If there is a receipt, session is not active anymore and we will use receiptChargingLineItems
  const chargingPrice = () => {
    if (!receipt) {
      return getTotalPrice(
        activeSessionLineItems
          .filter(isChargingItem)
          .map((item: CostBreakdownItem) => item.periodCost),
      );
    } else {
      return getTotalPrice(
        receiptChargingLineItems
          .filter(isChargingItem)
          .map((item: ReceiptLineItemDto) => item.totalPrice),
      );
    }
  };

  // If there is no receipt, session is still active and we will use activeSessionLineItems
  // If there is a receipt, session is not active anymore and we will use receiptIdleLineItems
  const idlePrice = () => {
    if (!receipt) {
      return getTotalPrice(
        activeSessionLineItems.filter(isIdleItem).map((item: CostBreakdownItem) => item.periodCost),
      );
    } else {
      return getTotalPrice(
        receiptIdleLineItems.filter(isIdleItem).map((item: ReceiptLineItemDto) => item.totalPrice),
      );
    }
  };

  // If there is no receipt, session is still active and we will use activeSessionLineItems
  // If there is a receipt, session is not active anymore and we will use receiptChargingLineItems
  const sessionFeePrice = () => {
    if (!receipt) {
      return getTotalPrice(
        activeSessionLineItems
          .filter(isSessionFeeItem)
          .map((item: CostBreakdownItem) => item.periodCost),
      );
    } else {
      return getTotalPrice(
        receiptChargingLineItems
          .filter(isSessionFeeItem)
          .map((item: ReceiptLineItemDto) => item.totalPrice),
      );
    }
  };

  // promo discount applied to the live session (no receipt yet).
  // the session's runningTotals carries the discount details once a promo code is applied.
  const sessionDiscounts = !receipt && session?.runningTotals?.discounts;
  const firstDiscount = sessionDiscounts?.[0];
  const activeDiscountPercent =
    firstDiscount?.discount?.method === 'PERCENTAGE' ? firstDiscount.discount.value : 0;
  const activePromoCodeName = firstDiscount?.discount?.promoCode;
  const activeDiscountAmount = firstDiscount?.amount || 0;

  const activeSubtotal = sessionFeePrice() + chargingPrice() + idlePrice();

  // calculate running cost on charget connector.
  // it must be sum of session fees, charging, and idling fees, minus any active promo discount.
  const runningCost = useMemo(() => {
    if (receipt?.total !== undefined) return receipt.total;
    if (session) return activeSubtotal - activeDiscountAmount;
    return 0;
  }, [receipt?.total, session, activeSubtotal, activeDiscountAmount]);

  // customise hours details text for session items.

  const readableDuration = (
    durationInSeconds: number,
    pricingType: string,
    energyConsumed: number,
  ) => {
    if (pricingType === PricingType.BILLED_BY_KWH) {
      return `${energyConsumed.toFixed(4)} kWh`;
    }
    var totalMinutes = Math.round(durationInSeconds / 60);
    var hour = Math.floor(totalMinutes / 60);
    var minute = totalMinutes % 60;
    if (hour < 1) {
      return `${minute} min`;
    }
    return `${hour} hr ${minute} min`;
  };

  const hoursDescForActiveSession = (item: CostBreakdownItem) => {
    return hourDesc(
      item.periodStart,
      item.periodEnd,
      item.pricingType,
      item.unitPrice,
      item.periodEnergyKilowattHours,
    );
  };

  const hoursDescForReceipt = (item: ReceiptLineItemDto) => {
    return hourDesc(
      item.startTime,
      item.stopTime,
      item.unitPricingType,
      item.unitPrice,
      item.energyDelivered,
    );
  };

  const hourDesc = (
    startTime: string,
    stopTime: string,
    pricingType: string,
    unitPrice: number,
    energyConsumed: number,
  ) => {
    const start = moment(startTime, 'YYYY-MM-DDTHH:mm:ssZ').toDate();
    const end = moment(stopTime, 'YYYY-MM-DDTHH:mm:ssZ').toDate();
    const formatStart = moment(start).format('h:mm A');
    const formatEnd = moment(end).format('h:mm A');

    const timeDiff = moment
      .duration(
        moment(stopTime, 'YYYY-MM-DDTHH:mm:ssZ').diff(moment(startTime, 'YYYY-MM-DDTHH:mm:ssZ')),
      )
      .asSeconds();

    const isFree = pricingType === RuleTypes.FREE;

    const readablePrice = isFree
      ? sessionPricingType(pricingType)
      : `$${fixedPrice(unitPrice)}/${sessionPricingType(pricingType)}`;

    return {
      details: `${readableDuration(timeDiff, pricingType, energyConsumed)} @ ${readablePrice}`,
      duration: ` ${formatStart} - ${formatEnd}`,
    };
  };

  const readableTaxDescriptions = (taxCode: string, percentage: any) => {
    switch (taxCode) {
      case 'sales tax':
        return `Sales tax (${percentage}%) incl.`;
      default:
        return `${taxCode} (${percentage}%) incl.`;
    }
  };

  const formatNumber = (input: number, decimalCount: number) => {
    try {
      return input.toFixed(decimalCount);
    } catch (e) {
      return '0.00';
    }
  };

  /**
   * Renders the discount line item with promo code name and percentage.
   * Used for both active sessions and finalized receipts.
   */
  const renderDiscountLine = (
    promoCodeName: string | undefined,
    discountPercent: number,
    discountAmount: number,
  ) => (
    <Grid container item className={classes.mt12} style={{ marginBottom: '16px' }}>
      <Grid item xs={8}>
        <span className={clsx(classes.weight500, classes.green)}>
          {promoCodeName
            ? `${promoCodeName} (${formatDiscountPercentageLabel(discountPercent, i18n.t)})`
            : `${i18n.t('receipt_discount')} (${formatDiscountPercentageLabel(discountPercent, i18n.t)})`}
        </span>
      </Grid>
      <Grid container item xs={4} justifyContent='flex-end'>
        <span className={clsx(classes.weight500, classes.green)}>{`- $${formatNumber(
          discountAmount,
          2,
        )}`}</span>
      </Grid>
    </Grid>
  );

  // If there is no receipt, session is still active and we will use activeSessionLineItems
  // If there is a receipt, session is not active anymore and we will use receiptChargingLineItems
  const renderChargingLineItems = () => {
    if (!receipt) {
      // eslint-disable-next-line array-callback-return
      return activeSessionLineItems
        .filter(isChargingItem)
        .map((item: CostBreakdownItem, index: number) => {
          const hours = hoursDescForActiveSession(item);
          return (
            <SessionListItem
              key={`charging-${index}`}
              title={hours.details}
              amount={`$${fixedPrice(item.periodCost)}`}
              styleClass={classes.listText}
              duration={hours.duration}
            />
          );
        });
    } else {
      return receiptChargingLineItems
        .filter(isChargingItem)
        .map((item: ReceiptLineItemDto, index: number) => {
          const hours = hoursDescForReceipt(item);
          return (
            <SessionListItem
              key={`charging-receipt-${index}`}
              title={hours.details}
              amount={`$${fixedPrice(item.totalPrice)}`}
              styleClass={classes.listText}
              duration={hours.duration}
            />
          );
        });
    }
  };

  // If there is no receipt, session is still active and we will use activeSessionLineItems
  // If there is a receipt, session is not active anymore and we will use receiptIdleLineItems
  const renderIdleLineItems = () => {
    if (!receipt) {
      // eslint-disable-next-line array-callback-return
      return activeSessionLineItems
        .filter(isIdleItem)
        .map((item: CostBreakdownItem, index: number) => {
          const hours = hoursDescForActiveSession(item);
          return (
            <SessionListItem
              key={`idle-${index}`}
              title={hours.details}
              amount={`$${fixedPrice(item.periodCost)}`}
              styleClass={classes.listText}
              duration={hours.duration}
            />
          );
        });
    } else {
      return receiptIdleLineItems
        .filter(isIdleItem)
        .map((item: ReceiptLineItemDto, index: number) => {
          const hours = hoursDescForReceipt(item);
          return (
            <SessionListItem
              key={`idle-receipt-${index}`}
              title={hours.details}
              amount={`$${fixedPrice(item.totalPrice)}`}
              styleClass={classes.listText}
              duration={hours.duration}
            />
          );
        });
    }
  };

  const isThereIdleLineItem = () => {
    if (!receipt) {
      return activeSessionLineItems.some(isIdleItem);
    } else {
      return receiptIdleLineItems.some(isIdleItem);
    }
  };

  const isThereSessionFeeLineItem = () => {
    if (!receipt) {
      return activeSessionLineItems.some(isSessionFeeItem);
    } else {
      return receiptChargingLineItems.some(isSessionFeeItem);
    }
  };

  return (
    <Grid container className={classes.root}>
      <Grid container style={{ paddingBottom: '16px' }}>
        <img src={Receipt} alt='receipt_icon' />
        <span
          style={{
            color: '#202223',
            fontSize: '16px',
            fontFamily: 'Inter',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '24px',
            paddingLeft: '8px',
          }}
        >
          {i18n.t('receipt_cost_detail')}
        </span>
      </Grid>
      <Grid className={classes.listWrapper} style={{ width: '100%' }}>
        {isThereSessionFeeLineItem() && (
          <SessionListItem
            title={i18n.t('session_fee')}
            amount={`$${fixedPrice(sessionFeePrice())}`}
            styleClass={classes.listHead}
          />
        )}
        <SessionListItem
          title={i18n.t('charging')}
          amount={`$${fixedPrice(chargingPrice())}`}
          styleClass={classes.listHead}
        />
        {renderChargingLineItems()}
        {isThereIdleLineItem() && (
          <SessionListItem
            title={'Idle fees'}
            amount={`$${fixedPrice(idlePrice())}`}
            styleClass={classes.listHead}
          />
        )}
        {renderIdleLineItems()}
        {activeDiscountPercent > 0 &&
          renderDiscountLine(activePromoCodeName, activeDiscountPercent, activeDiscountAmount)}
        <Grid container className={classes.list}>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        {!isChargingBySelf && (
          <Grid container item className={classes.pt16}>
            <Grid item xs={8}>
              <span className={classes.weight500}>{i18n.t('receipt_subtotal')}</span>
            </Grid>
            <Grid container item xs={4} justifyContent='flex-end'>
              <span className={classes.weight500}>{`$${formatNumber(receipt?.subTotal, 2)}`}</span>
            </Grid>
          </Grid>
        )}
        {receipt?.discountPercentage > 0 &&
          !isChargingBySelf &&
          renderDiscountLine(
            receipt.promoCode,
            receipt.discountPercentage,
            receipt.subTotal - receipt.total,
          )}

        {receipt?.taxRates &&
          !isChargingBySelf &&
          Object.entries(receipt.taxRates).map(([key, value]) => {
            return (
              <Grid key={key} container item className={classes.mt12}>
                <Grid item xs={8}>
                  <span className={classes.weight500}>{readableTaxDescriptions(key, value)}</span>
                </Grid>
                <Grid container item xs={4} justifyContent='flex-end'>
                  <span className={clsx(classes.weight500)}>{`$${formatNumber(
                    receipt.taxedAmounts[key],
                    2,
                  )}`}</span>
                </Grid>
              </Grid>
            );
          })}
        {!isChargingBySelf && (
          <Grid container item className={clsx(classes.borderBottom2, classes.mt16)}></Grid>
        )}
        <SessionListItem
          title={isChargingBySelf ? i18n.t('running_total') : 'Total'}
          amount={`$${fixedPrice(runningCost)}`}
          styleClass={clsx(classes.pt16, classes.listHead)}
          dataTestId={dataTestIds.chargerPage.totalAmountLabel}
        />
      </Grid>
    </Grid>
  );
};

export default withStyles(DetailsStyles)(ChargerSessionDetails);
